import React, { useEffect, useRef, useState } from "react";
import { Downloader, Parser, Player } from "svga.lite";
import "./LiveGiftsOverlay.css";

function LiveGiftSvga({ gift }) {
  const canvasRef = useRef(null);
  const playerRef = useRef(null);
  const parserRef = useRef(null);
  const downloaderRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let mounted = true;

    const cleanup = () => {
      try {
        playerRef.current?.clear?.();
      } catch (_) {}
      try {
        playerRef.current?.destroy?.();
      } catch (_) {}
      try {
        parserRef.current?.destroy?.();
      } catch (_) {}
      try {
        downloaderRef.current?.destroy?.();
      } catch (_) {}

      playerRef.current = null;
      parserRef.current = null;
      downloaderRef.current = null;
    };

    const run = async () => {
      const assetType = String(gift?.assetType || "").toLowerCase();
      const assetUrl = String(gift?.assetUrl || "").trim();

      setFailed(false);

      if (assetType !== "svga" || !assetUrl || !canvasRef.current) return;

      try {
        const downloader = new Downloader();
        const parser = new Parser();
        const player = new Player(canvasRef.current);

        downloaderRef.current = downloader;
        parserRef.current = parser;
        playerRef.current = player;

        const fileData = await downloader.get(assetUrl);
        const svgaData = await parser.do(fileData);

        if (!mounted) return;

        player.set({ loop: 1, fillMode: "forwards" });
        await player.mount(svgaData);
        player.start();
      } catch (err) {
        console.error("LiveGiftSvga load error:", err);
        if (mounted) setFailed(true);
        cleanup();
      }
    };

    run();

    return () => {
      mounted = false;
      cleanup();
    };
  }, [gift]);

  const imageUrl = String(gift?.previewUrl || "").trim();
  const hasSvga =
    String(gift?.assetType || "").toLowerCase() === "svga" &&
    String(gift?.assetUrl || "").trim();

  if (!hasSvga || failed) {
    return (
      <div className="livegift-icon">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={gift?.name || "gift"}
            className="livegift-preview"
            draggable="false"
          />
        ) : (
          <span className="livegift-fallback-emoji">{gift?.icon || "🎁"}</span>
        )}
      </div>
    );
  }

  return (
    <div className="livegift-icon livegift-icon-svga">
      <canvas ref={canvasRef} className="livegift-svga-host" />
    </div>
  );
}

export default function LiveGiftsOverlay({ gifts = [] }) {
  if (!Array.isArray(gifts) || gifts.length === 0) return null;

  return (
    <div className="livegifts-root">
      {gifts.map((gift) => (
        <div
          key={gift.key}
          className={`livegift-item ${gift.bump ? "bump" : ""} ${
            gift.exiting ? "exiting" : ""
          } ${gift.leaving ? "leaving" : ""}`}
        >
          <LiveGiftSvga gift={gift} />

          <div className="livegift-content">
            <div className="livegift-name">{gift.name || "Gift"}</div>

            <div className="livegift-meta">
              <span className="livegift-sender">
                {gift.sender || "User"}
              </span>

              {gift.count > 1 && (
                <span className="livegift-count">x{gift.count}</span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}